import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/router";
import {
    generateFilterObject,
    MAIN_PAGE_ADBLOCK_POSITIONS,
    PAGE_LIMIT_MAIN_SCREEN
} from "@components/Features/catalog/catalog-utils";
import BrandsList from "@components/Features/MainPage/BrandsList/BrandsList";
import MainFilter from "@components/Features/MainPage/MainFilter/MainFilter";
import MainPageTitle from "@components/Features/MainPage/MainPageTitle/MainPageTitle";
import { getCreatedTime, sortValueToQueryParam } from "@components/Screens/CatalogScreen/lib";
import { clearFilters, optimizeFilters } from "@app/store/filtersSlice";
import { useGetOffersQuery } from "@app/store/offersApi";
import { AppState } from "@app/store/store";
import { CategoryFilter } from "@features/category-filter";
import { generateCatalogUri } from "@entities/catalog";
import Car from "@shared/lib/interfaces/car.interface";
import MainScreenCatalog from "./MainScreenCatalog";
import classes from "./MainScreen.module.scss";

const MainScreen = () => {
    const [page, setPage] = React.useState(1);
    const dispatch = useDispatch();
    const router = useRouter();

    const mainScreenTimeSort = useSelector((state: AppState) => state.filters.mainScreenTimeSort);
    const mainScreenAllParamsSort = useSelector(
        (state: AppState) => state.filters.mainScreenAllParamsSort
    );

    const [createdTimePrev, setCreatedTimePrev] = useState<string | null>("");
    useEffect(() => {
        setCreatedTimePrev(getCreatedTime(mainScreenTimeSort));
    }, [mainScreenTimeSort]);
    const createdParamSort = sortValueToQueryParam(mainScreenAllParamsSort);
    const limit =
        page === 1
            ? PAGE_LIMIT_MAIN_SCREEN - MAIN_PAGE_ADBLOCK_POSITIONS.length
            : PAGE_LIMIT_MAIN_SCREEN;

    const {
        brand: brandValue,
        model: modelValue,
        yearFrom: yearFromValue,
        yearTo: yearToValue,
        body: bodyValue,
        transmission: transmissionValue,
        engineType: engineTypeValue,
        drive: driveValue,
        engineVolumeFrom: engineVolumeFromValue,
        engineVolumeTo: engineVolumeToValue,
        wheel: wheelValue,
        priceFrom: priceFromValue,
        priceTo: priceToValue,
        mileageFrom: mileageFromValue,
        mileageTo: mileageToValue,
        horsePowerFrom: horsePowerFromValue,
        horsePowerTo: horsePowerToValue,
        generation: generationValue,
        ownersQntFrom: ownerQntFrom,
        ownersQntTo,
        sourceId,
        sellerType,
        city: citiesValue,
        country,
        hideNoImages
    } = useSelector((state: AppState) => state.filters);

    const [quantityFilters, setQuantityFilters] = React.useState<any>([]);
    useEffect(() => {
        const newFilters = generateFilterObject({
            brandValue: brandValue?.id,
            modelValue: modelValue?.id,
            generationValue: generationValue?.id,
            yearFromValue,
            yearToValue,
            engineVolumeFromValue,
            engineVolumeToValue,
            bodyValue,
            transmissionValue,
            engineTypeValue,
            driveValue,
            wheelValue,
            priceFromValue,
            priceToValue,
            mileageFromValue,
            mileageToValue,
            horsePowerFromValue,
            horsePowerToValue,
            ownerQntFrom,
            ownersQntTo,
            citiesValue,
            sourceId,
            sellerType,
            country,
            hideNoImages
        });

        setQuantityFilters(newFilters);
    }, [
        brandValue,
        modelValue,
        yearFromValue,
        yearToValue,
        bodyValue,
        transmissionValue,
        engineTypeValue,
        driveValue,
        wheelValue,
        priceFromValue,
        priceToValue,
        mileageFromValue,
        mileageToValue,
        horsePowerFromValue,
        horsePowerToValue,
        generationValue,
        ownerQntFrom,
        ownersQntTo,
        citiesValue,
        engineVolumeFromValue,
        engineVolumeToValue,
        sourceId,
        sellerType,
        country,
        hideNoImages
    ]);

    useEffect(() => {
        setPage(1);
        setDataMemory([]);
    }, [mainScreenTimeSort, mainScreenAllParamsSort]);

    const { data, error, isLoading, isFetching } = useGetOffersQuery(
        {
            page,
            sort: createdParamSort,
            created_at: createdTimePrev,
            hideNoImages: true,
            limit
        },
        {
            skip: createdTimePrev === "",
            refetchOnMountOrArgChange: true
        }
    );

    const [dataMemory, setDataMemory] = React.useState<Car[]>([]);

    useEffect(() => {
        if (data) {
            setDataMemory((prev) => [...prev, ...data.offers]);
        }
    }, [data]);

    const showMoreButton = dataMemory.length < data?.meta.total;

    const [previewQnt, setPreviewQnt] = useState<null | number>(0);
    const {
        data: updatePreviewQntData,
        isLoading: updatePreviewQntIsLoading,
        isFetching: updatePreviewQntIsFetching
    } = useGetOffersQuery({
        page: page,
        ...quantityFilters
    });

    useEffect(() => {
        setPreviewQnt(updatePreviewQntData?.meta?.total);
    }, [updatePreviewQntData]);

    const onFilterSubmit = () => {
        dispatch(optimizeFilters());
        const catalogUri = generateCatalogUri(
            country,
            citiesValue,
            brandValue?.alias,
            modelValue?.alias,
            generationValue?.generation,
            generationValue?.restyleNumber,
            {
                bodyType: bodyValue,
                priceFrom: priceFromValue,
                priceTo: priceToValue
            }
        );
        router.push(catalogUri);
    };

    return (
        <div className={classes["main-screen"]}>
            <div className={classes["main-screen__bg"]} />
            <div className={classes["main-screen__blackout"]} />

            <div className={classes["main-screen__content"]}>
                <MainPageTitle />
                <MainFilter
                    previewQnt={previewQnt}
                    updatePreviewQntIsLoading={updatePreviewQntIsLoading}
                    isPreviewQntFetching={updatePreviewQntIsFetching}
                    onFilterSubmit={onFilterSubmit}
                    resetFilters={() => {
                        dispatch(clearFilters());
                    }}
                />
                <BrandsList />
                <CategoryFilter />
                <MainScreenCatalog
                    error={error}
                    isLoading={isLoading}
                    data={data}
                    mainScreenAllParamsSort={mainScreenAllParamsSort}
                    mainScreenTimeSort={mainScreenTimeSort}
                    isFetching={isFetching}
                    dataMemory={dataMemory}
                    showMoreButton={showMoreButton}
                    page={page}
                    setPage={setPage}
                    setDataMemory={setDataMemory}
                />
            </div>
        </div>
    );
};

export default MainScreen;